import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  Answer,
  AddressFieldValidationConfig,
  QuestionType,
  WorldAddressQuestion,
} from 'src/app/core/models/questionnaire.model';
import { StructuredAddress } from 'src/app/core/services/address.service';
import { PlaceAdditionalInfo } from 'src/app/core/services/google-places.service';

/**
 * CPK-4914: what the profile should do with the address on save.
 * - `unchanged`: the user didn't touch the address — keep the stored one as is.
 * - `invalid`:   the user started a new address but it's incomplete — block the save.
 * - `changed`:   a complete new address, in the WORLD_ADDRESS stored format.
 */
export type ProfileAddressState =
  | { status: 'unchanged' }
  | { status: 'invalid' }
  | { status: 'changed'; address: StructuredAddress };

/**
 * CPK-4914: the profile has no questionnaire question to carry `fieldValidation`, so it
 * uses the only rule set WORLD_ADDRESS questions use in production.
 */
export const PROFILE_ADDRESS_FIELD_VALIDATION: AddressFieldValidationConfig = {
  _global: { denyPattern: '[!@#$%&*]' },
  streetName: { minLength: 3, denyPattern: '[!@#$%&*]' },
  streetNumber: { denyPattern: '[!@#$%&*]', requireAlphanumeric: true },
  postalCode: { denyPattern: '[!@#$%&*]', brazilExactDigits: 8 },
};

/**
 * Profile address input (CPK-4914): the WORLD_ADDRESS question component — Google Places
 * autocomplete plus the "I can't find my address" ViaCEP mode — reused as is, driven by a
 * synthetic question so the questionnaire flow is not touched.
 *
 * The address is optional: an untouched input keeps the stored address. Once the user starts
 * a new one, the WORLD_ADDRESS required fields apply. Addresses that didn't come from this
 * input (free text, imports, PoA) are not loaded into the fields — they're shown as a
 * reference above the search instead.
 */
@Component({
  selector: 'app-profile-address-input',
  templateUrl: './profile-address-input.component.html',
  styleUrls: ['./profile-address-input.component.scss'],
})
export class ProfileAddressInputComponent implements OnChanges {
  @Input() address: StructuredAddress | null | undefined;
  @Output() stateChange = new EventEmitter<ProfileAddressState>();

  /** Single-item list so a new address object re-creates the inner component (it only reads its answer on init). */
  questions: WorldAddressQuestion[] = [];
  currentAddressText = '';

  private initialAnswerSnapshot = '';
  private lastStatus: ProfileAddressState['status'] = 'unchanged';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['address']) {
      this.build(this.address ?? null);
    }
  }

  onAnswerChange(): void {
    const question = this.questions[0];
    const answer = question?.answer;
    if (!answer) return;

    if (this.snapshot(answer) === this.initialAnswerSnapshot) {
      this.emit({ status: 'unchanged' });
      return;
    }

    const info = answer.addtional_information as PlaceAdditionalInfo | null | undefined;
    if (!info) {
      // Typing in the search without picking a result, or back to an empty search.
      const typed = String(answer.value ?? '').trim();
      this.emit(typed ? { status: 'invalid' } : { status: 'unchanged' });
      return;
    }

    if (!question.isValid) {
      this.emit({ status: 'invalid' });
      return;
    }

    this.emit({ status: 'changed', address: this.toStructuredAddress(info) });
  }

  /**
   * WORLD_ADDRESS emits on its own init (inside the parent's change detection), so only
   * status transitions are emitted — plus every `changed`, whose address may differ.
   */
  private emit(state: ProfileAddressState): void {
    if (state.status !== 'changed' && state.status === this.lastStatus) return;
    this.lastStatus = state.status;
    this.stateChange.emit(state);
  }

  private build(address: StructuredAddress | null): void {
    const prefill = this.isFromAddressInput(address);
    const answer: Answer = prefill
      ? { value: this.compose(address!), addtional_information: { ...address } }
      : { value: null };

    this.currentAddressText = prefill || !address ? '' : this.describeLegacy(address);
    this.initialAnswerSnapshot = this.snapshot(answer);
    if (this.lastStatus !== 'unchanged') {
      // A new stored address (e.g. after a save) resets the parent; deferred because
      // ngOnChanges runs inside the parent's change detection.
      this.lastStatus = 'unchanged';
      Promise.resolve().then(() => this.stateChange.emit({ status: 'unchanged' }));
    }
    this.questions = [
      {
        id: 'profile-address',
        created_at: new Date(),
        name: 'Address',
        slug: 'profile_address',
        statement: '',
        response_type: 'WORLD_ADDRESS' as unknown as QuestionType,
        questionnaire_id: '',
        elective: true,
        configuration: { fieldValidation: PROFILE_ADDRESS_FIELD_VALIDATION },
        answer,
        isValid: true,
      },
    ];
  }

  /** Only addresses captured by WORLD_ADDRESS (or its validation prompt) carry `resolvedVia`. */
  private isFromAddressInput(address: StructuredAddress | null): boolean {
    return typeof address?.['resolvedVia'] === 'string' && !!address['resolvedVia'];
  }

  private snapshot(answer: Answer): string {
    return JSON.stringify({
      value: String(answer.value ?? '').trim(),
      info: answer.addtional_information ?? null,
    });
  }

  /** Same composite WORLD_ADDRESS keeps in `answer.value` in manual mode. */
  private compose(address: StructuredAddress): string {
    const { streetNumber, streetName, city, state, postalCode, country } = address;
    return [streetNumber, streetName, city, state, postalCode, country].filter(Boolean).join(', ');
  }

  /**
   * Same source the client infocard shows (and contracts/statements read): the
   * `address_line_1` format, else the structured fields. PoA imports also keep the raw
   * document text in `full_address`, which can disagree with the geocoded fields — it is
   * only shown when the structured fields have no street, and never mixed with them.
   */
  private describeLegacy(address: StructuredAddress): string {
    const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
    const join = (parts: string[]) => parts.filter(Boolean).join(', ');

    if (text(address.address_line_1)) {
      return join([
        text(address.address_line_1),
        text(address.city),
        text(address.postcode) || text(address.postalCode),
        text(address.country),
      ]);
    }

    const structured = join([
      [text(address.streetNumber), text(address.streetName)].filter(Boolean).join(' '),
      text(address.unitNumber),
      text(address.neighborhood),
      text(address.city),
      text(address.state),
      text(address.postalCode) || text(address.postcode),
      text(address.country),
    ]);

    if (text(address.streetName)) return structured;
    return text(address['full_address']) || structured;
  }

  private toStructuredAddress(info: PlaceAdditionalInfo): StructuredAddress {
    return {
      streetNumber: info.streetNumber ?? '',
      streetName: info.streetName ?? '',
      unitNumber: info.unitNumber ?? '',
      buildingName: info.buildingName ?? '',
      referencePoint: info.referencePoint ?? '',
      neighborhood: info.neighborhood ?? '',
      city: info.city ?? '',
      state: info.state ?? '',
      county: info.county ?? '',
      country: info.country ?? '',
      postalCode: info.postalCode ?? '',
      latitude: info.latitude ?? null,
      longitude: info.longitude ?? null,
      resolvedVia: info.resolvedVia,
    };
  }
}
